import { type FormEvent, useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Spinner } from '@/components/ui/Spinner';
import { isSpecialtyRelevant } from '@/lib/field-relevance';
import {
  type PartCategory,
  type PartDetail,
  type PartUpdateInput,
  usePartCategories,
  useUpdatePart,
} from '@/lib/parts';

interface EditPartModalProps {
  orgSlug: string;
  part: PartDetail;
  onClose: () => void;
  onSaved: () => void;
}

const MOUNTING_OPTIONS = [
  { value: '', label: '— unset —' },
  { value: 'smd', label: 'SMD' },
  { value: 'tht', label: 'Through-hole (THT)' },
  { value: 'chassis', label: 'Chassis' },
  { value: 'other', label: 'Other' },
];

const LIFECYCLE_OPTIONS = [
  { value: 'unknown', label: 'Unknown' },
  { value: 'active', label: 'Active' },
  { value: 'nrnd', label: 'Not recommended for new design' },
  { value: 'obsolete', label: 'Obsolete' },
  { value: 'preview', label: 'Preview' },
];

const ROHS_OPTIONS = [
  { value: '', label: '— unknown —' },
  { value: 'true', label: 'RoHS compliant' },
  { value: 'false', label: 'Not compliant' },
];

const VISIBILITY_OPTIONS = [
  { value: 'private', label: 'Private (only me)' },
  { value: 'org', label: 'Organization (default)' },
  { value: 'public', label: 'Public (cross-org catalog)' },
];

/**
 * Edit form for an existing part. Mirrors the structure of NewPartModal
 * but skips the create-only flows (inventory + images + duplicate
 * detection) — those are managed against an already-saved part via the
 * detail drawer's dedicated sections.
 *
 * Inputs are seeded from `part` on mount and dispatched as a single
 * PATCH request that includes only changed fields. Optimistic locking
 * is handled server-side via row_version.
 */
export function EditPartModal({ orgSlug, part, onClose, onSaved }: EditPartModalProps) {
  const update = useUpdatePart(orgSlug, part.id);
  const categories = usePartCategories(orgSlug);

  // Identity
  const [mfgPn, setMfgPn] = useState(part.mfg_pn ?? '');
  const [manufacturer, setManufacturer] = useState(part.manufacturer ?? '');
  const [altMfgPn, setAltMfgPn] = useState(part.alt_mfg_pn ?? '');
  const [altManufacturer, setAltManufacturer] = useState(part.alt_manufacturer ?? '');
  const [description, setDescription] = useState(part.description ?? '');

  // Classification
  const [categoryId, setCategoryId] = useState<string>(
    part.category_id !== null ? String(part.category_id) : '',
  );
  const [value, setValue] = useState(part.value ?? '');
  const [tolerance, setTolerance] = useState(part.tolerance ?? '');
  const [dielectric, setDielectric] = useState(part.dielectric ?? '');
  const [lifecycle, setLifecycle] = useState(part.lifecycle);
  const [tags, setTags] = useState((part.tags ?? []).join(', '));

  // Physical
  const [packageStr, setPackage] = useState(part.package ?? '');
  const [mounting, setMounting] = useState(part.mounting ?? '');
  const [polarized, setPolarized] = useState<string>(
    part.polarized === null ? '' : part.polarized ? 'true' : 'false',
  );
  const [pinCount, setPinCount] = useState(
    part.pin_count !== null ? String(part.pin_count) : '',
  );

  // Electrical
  const [voltageV, setVoltageV] = useState(numStr(part.voltage_v));
  const [voltageVMax, setVoltageVMax] = useState(numStr(part.voltage_v_max));
  const [powerW, setPowerW] = useState(numStr(part.power_w));
  const [currentA, setCurrentA] = useState(numStr(part.current_a));
  const [vf, setVf] = useState(numStr(part.vf));
  const [vr, setVr] = useState(numStr(part.vr));
  const [frequencyHz, setFrequencyHz] = useState(numStr(part.frequency_hz));
  const [tempMin, setTempMin] = useState(numStr(part.temp_min_c));
  const [tempMax, setTempMax] = useState(numStr(part.temp_max_c));

  // Type-specific specs (migration 0013). Edit modal shows all
  // unconditionally — it's the "advanced view" where category-aware
  // hiding would just block someone fixing an incorrect category.
  const [polarity, setPolarity] = useState(part.polarity ?? '');
  const [outputVoltageV, setOutputVoltageV] = useState(numStr(part.output_voltage_v));
  const [outputVoltageVMax, setOutputVoltageVMax] = useState(
    numStr(part.output_voltage_v_max),
  );
  const [outputCurrentA, setOutputCurrentA] = useState(numStr(part.output_current_a));
  const [regulatorOutputType, setRegulatorOutputType] = useState(
    part.regulator_output_type ?? '',
  );
  const [voltageClampV, setVoltageClampV] = useState(numStr(part.voltage_clamp_v));
  const [holdCurrentA, setHoldCurrentA] = useState(numStr(part.hold_current_a));
  const [tripCurrentA, setTripCurrentA] = useState(numStr(part.trip_current_a));
  const [color, setColor] = useState(part.color ?? '');
  const [wavelengthNm, setWavelengthNm] = useState(numStr(part.wavelength_nm));
  const [pitchMm, setPitchMm] = useState(numStr(part.pitch_mm));
  // 0014 spec columns.
  const [luminousIntensityMcd, setLuminousIntensityMcd] = useState(
    numStr(part.luminous_intensity_mcd),
  );
  const [loadCapacitancePf, setLoadCapacitancePf] = useState(
    numStr(part.load_capacitance_pf),
  );
  const [rdsOnOhms, setRdsOnOhms] = useState(numStr(part.rds_on_ohms));
  const [hfe, setHfe] = useState(numStr(part.hfe));

  // Sourcing / metadata
  const [rohs, setRohs] = useState<string>(
    part.rohs_compliant === null ? '' : part.rohs_compliant ? 'true' : 'false',
  );
  const [datasheetUrl, setDatasheetUrl] = useState(part.datasheet_url ?? '');
  const [imageUrl, setImageUrl] = useState(part.image_url ?? '');
  const [visibility, setVisibility] = useState(part.visibility);
  const [notes, setNotes] = useState(part.notes ?? '');

  // KiCad library linkage
  const [kicadSymbol, setKicadSymbol] = useState(part.kicad_symbol ?? '');
  const [kicadFootprint, setKicadFootprint] = useState(part.kicad_footprint ?? '');
  const [kicadHas3dModel, setKicadHas3dModel] = useState(part.kicad_has_3d_model);
  const [kicadTracked, setKicadTracked] = useState(part.kicad_tracked);

  // Category-aware visibility — same rubric as NewPartModal so a
  // resistor edit doesn't surface the LED-only color/wavelength
  // fields. Toggle exposes the hidden fields when the user genuinely
  // needs to type into one.
  const [showAllFields, setShowAllFields] = useState(false);
  const selectedCategory = categories.data?.find(
    (c) => String(c.id) === categoryId,
  );
  const selectedSlug = selectedCategory?.slug ?? null;
  const showDielectric = isSpecialtyRelevant('dielectric', selectedSlug, showAllFields);
  const showVf = isSpecialtyRelevant('vf', selectedSlug, showAllFields);
  const showVr = isSpecialtyRelevant('vr', selectedSlug, showAllFields);
  const showPowerW = isSpecialtyRelevant('power_w', selectedSlug, showAllFields);
  const showPolarity = isSpecialtyRelevant('polarity', selectedSlug, showAllFields);
  const showRegulatorOutput = isSpecialtyRelevant(
    'regulator_output', selectedSlug, showAllFields,
  );
  const showVoltageClamp = isSpecialtyRelevant(
    'voltage_clamp', selectedSlug, showAllFields,
  );
  const showFuseCurrents = isSpecialtyRelevant(
    'fuse_currents', selectedSlug, showAllFields,
  );
  const showLedColor = isSpecialtyRelevant('led_color', selectedSlug, showAllFields);
  const showPitch = isSpecialtyRelevant('pitch', selectedSlug, showAllFields);
  const showLuminous = isSpecialtyRelevant(
    'luminous_intensity', selectedSlug, showAllFields,
  );
  const showLoadCap = isSpecialtyRelevant(
    'load_capacitance', selectedSlug, showAllFields,
  );
  const showRdsOn = isSpecialtyRelevant('rds_on', selectedSlug, showAllFields);
  const showHfe = isSpecialtyRelevant('hfe', selectedSlug, showAllFields);

  // Reset state when the underlying part changes (e.g., user closes the
  // modal, opens a different part, then re-opens the edit modal).
  useEffect(() => {
    /* state is seeded from `part` once on mount; intentional. */
  }, [part.id]);

  const buildPayload = (): PartUpdateInput => {
    const trimmed = (s: string): string | null => {
      const t = s.trim();
      return t === '' ? null : t;
    };
    const num = (s: string): number | null => {
      const t = s.trim();
      if (t === '') return null;
      const n = Number(t);
      return Number.isNaN(n) ? null : n;
    };
    const intOrNull = (s: string): number | null => {
      const t = s.trim();
      if (t === '') return null;
      const n = parseInt(t, 10);
      return Number.isNaN(n) ? null : n;
    };
    const boolOrNull = (s: string): boolean | null => {
      if (s === '') return null;
      return s === 'true';
    };

    return {
      row_version: part.row_version,
      mfg_pn: trimmed(mfgPn),
      manufacturer: trimmed(manufacturer),
      alt_mfg_pn: trimmed(altMfgPn),
      alt_manufacturer: trimmed(altManufacturer),
      description: trimmed(description),
      category_id: categoryId === '' ? null : Number(categoryId),
      value: trimmed(value),
      tolerance: trimmed(tolerance),
      dielectric: trimmed(dielectric),
      lifecycle,
      tags: tagsToArray(tags),
      package: trimmed(packageStr),
      mounting: mounting || null,
      polarized: boolOrNull(polarized),
      pin_count: intOrNull(pinCount),
      voltage_v: num(voltageV),
      voltage_v_max: num(voltageVMax),
      power_w: num(powerW),
      current_a: num(currentA),
      vf: num(vf),
      vr: num(vr),
      frequency_hz: num(frequencyHz),
      temp_min_c: num(tempMin),
      temp_max_c: num(tempMax),
      polarity: trimmed(polarity),
      output_voltage_v: num(outputVoltageV),
      output_voltage_v_max: num(outputVoltageVMax),
      output_current_a: num(outputCurrentA),
      regulator_output_type: trimmed(regulatorOutputType),
      voltage_clamp_v: num(voltageClampV),
      hold_current_a: num(holdCurrentA),
      trip_current_a: num(tripCurrentA),
      color: trimmed(color),
      wavelength_nm: num(wavelengthNm),
      pitch_mm: num(pitchMm),
      luminous_intensity_mcd: num(luminousIntensityMcd),
      load_capacitance_pf: num(loadCapacitancePf),
      rds_on_ohms: num(rdsOnOhms),
      hfe: num(hfe),
      rohs_compliant: boolOrNull(rohs),
      datasheet_url: trimmed(datasheetUrl),
      image_url: trimmed(imageUrl),
      visibility,
      notes: trimmed(notes),
      kicad_symbol: trimmed(kicadSymbol),
      kicad_footprint: trimmed(kicadFootprint),
      kicad_has_3d_model: kicadHas3dModel,
      kicad_tracked: kicadTracked,
    };
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await update.mutateAsync(buildPayload());
      onSaved();
    } catch {
      /* surfaced below */
    }
  };

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-stone-900/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 shadow-xl">
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-5 py-3">
          <h2 className="text-lg font-semibold">
            Edit <span className="font-mono">{part.part_number}</span>
          </h2>
          <div className="flex items-center gap-2">
            <label className="flex cursor-pointer select-none items-center gap-1 text-xs text-stone-500 dark:text-stone-400">
              <input
                type="checkbox"
                checked={showAllFields}
                onChange={(e) => setShowAllFields(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
              />
              Show all fields
            </label>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-4">
            <p className="text-xs text-stone-500 dark:text-stone-400">
              All fields are optional. Inventory, suppliers, and images are
              edited from their dedicated sections in the drawer.
            </p>

            <Section title="Identity">
              <Grid2>
                <Field label="Manufacturer PN" mono>
                  <Input value={mfgPn} onChange={(e) => setMfgPn(e.target.value)} />
                </Field>
                <Field label="Manufacturer">
                  <Input
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                  />
                </Field>
                <Field label="Alt Manufacturer PN" mono>
                  <Input value={altMfgPn} onChange={(e) => setAltMfgPn(e.target.value)} />
                </Field>
                <Field label="Alt Manufacturer">
                  <Input
                    value={altManufacturer}
                    onChange={(e) => setAltManufacturer(e.target.value)}
                  />
                </Field>
                <Field label="Description" span={2}>
                  <Input
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </Field>
              </Grid2>
            </Section>

            <Section title="Classification">
              <Grid2>
                <Field label="Category">
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    <option value="">— uncategorized —</option>
                    {renderCategoryOptions(categories.data)}
                  </select>
                </Field>
                <Field label="Lifecycle">
                  <select
                    value={lifecycle}
                    onChange={(e) => setLifecycle(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {LIFECYCLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Value">
                  <Input value={value} onChange={(e) => setValue(e.target.value)} />
                </Field>
                <Field label="Tolerance">
                  <Input value={tolerance} onChange={(e) => setTolerance(e.target.value)} />
                </Field>
                {showDielectric && (
                  <Field label="Dielectric">
                    <Input
                      value={dielectric}
                      onChange={(e) => setDielectric(e.target.value)}
                    />
                  </Field>
                )}
                {showPolarity && (
                  <Field label="Polarity / Type">
                    <Input
                      value={polarity}
                      onChange={(e) => setPolarity(e.target.value)}
                      placeholder="NPN, N-Channel, Unidirectional, …"
                    />
                  </Field>
                )}
                {showLedColor && (
                  <Field label="Color">
                    <Input
                      value={color}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="Red, Blue, Emerald Green, …"
                    />
                  </Field>
                )}
                {showLuminous && (
                  <>
                    <Field label="Wavelength (nm)">
                      <Input
                        type="number"
                        step="any"
                        value={wavelengthNm}
                        onChange={(e) => setWavelengthNm(e.target.value)}
                      />
                    </Field>
                    <Field label="Luminous intensity (mcd)">
                      <Input
                        type="number"
                        step="any"
                        value={luminousIntensityMcd}
                        onChange={(e) => setLuminousIntensityMcd(e.target.value)}
                      />
                    </Field>
                  </>
                )}
                <Field label="Tags (comma-separated)">
                  <Input value={tags} onChange={(e) => setTags(e.target.value)} />
                </Field>
              </Grid2>
            </Section>

            <Section title="Physical">
              <Grid2>
                <Field label="Package">
                  <Input value={packageStr} onChange={(e) => setPackage(e.target.value)} />
                </Field>
                <Field label="Mounting">
                  <select
                    value={mounting}
                    onChange={(e) => setMounting(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {MOUNTING_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Polarized">
                  <select
                    value={polarized}
                    onChange={(e) => setPolarized(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    <option value="">— unset —</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                </Field>
                <Field label="Pin count">
                  <Input
                    type="number"
                    min={0}
                    value={pinCount}
                    onChange={(e) => setPinCount(e.target.value)}
                  />
                </Field>
                {showPitch && (
                  <Field label="Pitch (mm)">
                    <Input
                      type="number"
                      step="any"
                      value={pitchMm}
                      onChange={(e) => setPitchMm(e.target.value)}
                    />
                  </Field>
                )}
              </Grid2>
            </Section>

            <Section title="Electrical">
              <Grid2>
                <Field label="Voltage (V)">
                  <Input
                    type="number"
                    step="any"
                    value={voltageV}
                    onChange={(e) => setVoltageV(e.target.value)}
                  />
                </Field>
                <Field label="Voltage max (V)">
                  <Input
                    type="number"
                    step="any"
                    value={voltageVMax}
                    onChange={(e) => setVoltageVMax(e.target.value)}
                  />
                </Field>
                {showPowerW && (
                  <Field label="Power (W)">
                    <Input
                      type="number"
                      step="any"
                      value={powerW}
                      onChange={(e) => setPowerW(e.target.value)}
                    />
                  </Field>
                )}
                <Field label="Current (A)">
                  <Input
                    type="number"
                    step="any"
                    value={currentA}
                    onChange={(e) => setCurrentA(e.target.value)}
                  />
                </Field>
                {showVf && (
                  <Field label="Vf (V)">
                    <Input
                      type="number"
                      step="any"
                      value={vf}
                      onChange={(e) => setVf(e.target.value)}
                    />
                  </Field>
                )}
                {showVr && (
                  <Field label="Vr (V)">
                    <Input
                      type="number"
                      step="any"
                      value={vr}
                      onChange={(e) => setVr(e.target.value)}
                    />
                  </Field>
                )}
                <Field label="Frequency (Hz)">
                  <Input
                    type="number"
                    step="any"
                    value={frequencyHz}
                    onChange={(e) => setFrequencyHz(e.target.value)}
                  />
                </Field>
                <Field label="Temp min/max (°C)">
                  <div className="flex gap-1">
                    <Input
                      type="number"
                      step="any"
                      value={tempMin}
                      onChange={(e) => setTempMin(e.target.value)}
                      placeholder="-40"
                    />
                    <Input
                      type="number"
                      step="any"
                      value={tempMax}
                      onChange={(e) => setTempMax(e.target.value)}
                      placeholder="125"
                    />
                  </div>
                </Field>
                {showVoltageClamp && (
                  <Field label="Vc clamp (V)">
                    <Input
                      type="number"
                      step="any"
                      value={voltageClampV}
                      onChange={(e) => setVoltageClampV(e.target.value)}
                    />
                  </Field>
                )}
                {showLoadCap && (
                  <Field label="Load capacitance (pF)">
                    <Input
                      type="number"
                      step="any"
                      value={loadCapacitancePf}
                      onChange={(e) => setLoadCapacitancePf(e.target.value)}
                    />
                  </Field>
                )}
                {showRdsOn && (
                  <Field label="RDS(on) (Ω)">
                    <Input
                      type="number"
                      step="any"
                      value={rdsOnOhms}
                      onChange={(e) => setRdsOnOhms(e.target.value)}
                    />
                  </Field>
                )}
                {showHfe && (
                  <Field label="hFE (DC current gain)">
                    <Input
                      type="number"
                      step="any"
                      value={hfe}
                      onChange={(e) => setHfe(e.target.value)}
                    />
                  </Field>
                )}
              </Grid2>
            </Section>

            {showRegulatorOutput && (
              <Section title="Regulator output">
                <Grid2>
                  <Field label="Output voltage (V)">
                    <Input
                      type="number"
                      step="any"
                      value={outputVoltageV}
                      onChange={(e) => setOutputVoltageV(e.target.value)}
                    />
                  </Field>
                  <Field label="Output voltage max (V)">
                    <Input
                      type="number"
                      step="any"
                      value={outputVoltageVMax}
                      onChange={(e) => setOutputVoltageVMax(e.target.value)}
                    />
                  </Field>
                  <Field label="Output current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={outputCurrentA}
                      onChange={(e) => setOutputCurrentA(e.target.value)}
                    />
                  </Field>
                  <Field label="Type">
                    <Input
                      value={regulatorOutputType}
                      onChange={(e) => setRegulatorOutputType(e.target.value)}
                      placeholder="Fixed, Adjustable, Programmable"
                    />
                  </Field>
                </Grid2>
              </Section>
            )}

            {showFuseCurrents && (
              <Section title="Fuse currents">
                <Grid2>
                  <Field label="Hold current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={holdCurrentA}
                      onChange={(e) => setHoldCurrentA(e.target.value)}
                    />
                  </Field>
                  <Field label="Trip current (A)">
                    <Input
                      type="number"
                      step="any"
                      value={tripCurrentA}
                      onChange={(e) => setTripCurrentA(e.target.value)}
                    />
                  </Field>
                </Grid2>
              </Section>
            )}

            <Section title="Sourcing & metadata">
              <Grid2>
                <Field label="RoHS">
                  <select
                    value={rohs}
                    onChange={(e) => setRohs(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {ROHS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Visibility">
                  <select
                    value={visibility}
                    onChange={(e) => setVisibility(e.target.value)}
                    className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm"
                  >
                    {VISIBILITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Datasheet URL" span={2}>
                  <Input
                    type="url"
                    value={datasheetUrl}
                    onChange={(e) => setDatasheetUrl(e.target.value)}
                  />
                </Field>
                <Field label="External image URL" span={2}>
                  <Input
                    type="url"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://… (only used when no uploaded images)"
                  />
                </Field>
              </Grid2>
            </Section>

            <Section title="KiCad library">
              <Grid2>
                <Field label="Symbol (LIB_ID)" mono>
                  <Input
                    value={kicadSymbol}
                    onChange={(e) => setKicadSymbol(e.target.value)}
                  />
                </Field>
                <Field label="Footprint (LIB_ID)" mono>
                  <Input
                    value={kicadFootprint}
                    onChange={(e) => setKicadFootprint(e.target.value)}
                  />
                </Field>
              </Grid2>
              <label className="mt-3 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={kicadHas3dModel}
                  onChange={(e) => setKicadHas3dModel(e.target.checked)}
                  className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                />
                Footprint has a 3D model attached
              </label>
              <label className="mt-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={kicadTracked}
                  onChange={(e) => setKicadTracked(e.target.checked)}
                  className="h-4 w-4 rounded border border-stone-300 dark:border-stone-700 text-forge-600 focus:ring-forge-500"
                />
                Tracked in KiCad (legacy flag — kept in sync if either LIB_ID is set)
              </label>
            </Section>

            <Section title="Notes">
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-3 py-2 text-sm focus:border-forge-500 focus:outline-none focus:ring-1 focus:ring-forge-500"
              />
            </Section>

            {update.isError && (
              <p className="text-sm text-red-600">{(update.error as Error).message}</p>
            )}
          </div>

          <div className="flex justify-end gap-2 border-t border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 px-5 py-3">
            <Button variant="ghost" type="button" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? (
                <>
                  <Spinner className="mr-2 h-4 w-4" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function numStr(n: number | null): string {
  if (n === null) return '';
  return String(n);
}

function tagsToArray(raw: string): string[] | null {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return parts.length === 0 ? null : parts;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Grid2({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-3 md:grid-cols-2">{children}</div>;
}

function Field({
  label,
  children,
  span,
  mono,
}: {
  label: string;
  children: React.ReactNode;
  span?: 2;
  mono?: boolean;
}) {
  return (
    <label className={span === 2 ? 'md:col-span-2 block' : 'block'}>
      <span className="mb-1 block text-xs font-medium text-stone-600 dark:text-stone-400">{label}</span>
      <div className={mono ? 'font-mono' : ''}>{children}</div>
    </label>
  );
}

function renderCategoryOptions(categories: PartCategory[] | undefined) {
  if (!categories) return null;
  // Same two-level optgroup pattern as NewPartModal: top-level rows
  // become group headers; subcategories slot into their parent's group.
  const top = categories.filter((c) => c.parent_id === null).sort((a, b) => a.sort_order - b.sort_order);
  return top.map((parent) => {
    const children = categories
      .filter((c) => c.parent_id === parent.id)
      .sort((a, b) => a.sort_order - b.sort_order);
    if (children.length === 0) {
      return (
        <option key={parent.id} value={parent.id}>
          {parent.name}
        </option>
      );
    }
    return (
      <optgroup key={parent.id} label={parent.name}>
        <option value={parent.id}>{parent.name} (general)</option>
        {children.map((child) => (
          <option key={child.id} value={child.id}>
            {child.name}
          </option>
        ))}
      </optgroup>
    );
  });
}
